/**
 * @fileoverview Key delivery: how a collection key reaches the person it was
 * shared with, and what the relay is allowed to know about it.
 *
 * Until this existed, a private collection worked for its author and for nobody
 * else. Everything else was built and proved; the key had no way to travel.
 *
 * ---
 *
 * THE PLAINTEXT TAGS CARRY THE RECIPIENT AND NOTHING ELSE, and that is the
 * decision this file is shaped around.
 *
 * The obvious share event names what it shares: an `a` tag with the collection's
 * address, so the recipient can find it without decrypting anything. That is
 * wrong for exactly the reason a private collection member never appears in a
 * tag. An address names an author, a kind and an identifier, so a list of the
 * addresses in somebody's shares is the shape of what they have been given: how
 * many private things, from whom, and for anything named by a human rather than
 * generated, what they are about.
 *
 * It is worse here than one level down, because a share event is ADDRESSED to a
 * person and therefore queryable by that person's key. `#p` is how the recipient
 * finds it, which means it is also how anybody else enumerates what that person
 * has been given.
 *
 * So a stranger reading every share event on the relay learns that two keys
 * exchanged something, and nothing else. No count of collections, no addresses,
 * no titles.
 *
 * ---
 *
 * THE PAYLOAD CARRIES THE ADDRESS AND THE KEY, SEALED TOGETHER, and the reason
 * they travel together is not convenience.
 *
 * The key is wrapped with the same envelope primitive everything else uses, and
 * an envelope NAMES WHAT IT OPENS. So the payload holds the address in one
 * field and the envelope in another, and opening it checks that the two agree.
 * A share whose stated address does not match the address sealed inside its key
 * is refused rather than followed, because following it would point a recipient
 * at one collection holding the key to a different one, and the failure would
 * surface as "this collection will not open" with nothing to say why.
 *
 * ---
 *
 * WHAT THIS DOES NOT DO, stated because the surrounding design already says it
 * twice and a third omission would look like an oversight rather than a limit:
 *
 * REVOCATION COVERS FUTURE VERSIONS ONLY. Removing a recipient means rotating
 * the collection key and re-issuing everyone else, and the person removed keeps
 * whatever they already opened. No share event can take a key back.
 *
 * DELETING A SHARE IS NOT REVOCATION. It stops a recipient who has not yet
 * opened it from finding it, and does nothing to one who has.
 */

import {
  type ContentKey,
  type Envelope,
  type SignerInterface,
  contentKeyToHex,
  unwrapKeyFromSender,
  wrapKeyForRecipient,
} from '@cloistr/auth/core';
import type { Event, EventTemplate, UnsignedEvent } from 'nostr-tools';


import { SHARE_KIND } from './kinds';
import { nextCreatedAt } from './publishPage';

import { fetchAll, publishEvent } from '@/services/nostr/relays';

/** What a share event's encrypted payload holds. */
interface SharePayload {
  /** The addressable coordinate of the thing being shared. */
  a: string;
  /** The key, wrapped to this recipient, naming the same address inside. */
  e: Envelope;
}

export interface Share {
  /** The address the key opens. Read from inside the payload, never from a tag. */
  address: string;
  key: ContentKey;
  /** Who sent it. The key was wrapped against this pubkey. */
  from: string;
}

export type SharePublishOutcome =
  | { status: 'shared'; recipient: string; acceptedBy: string[] }
  | { status: 'rejected'; reasons: Record<string, string> }
  | { status: 'signer-failed'; message: string };

/**
 * A name for the share event itself.
 *
 * OPAQUE AND RANDOM, for the same reason a private page's name is. The `d` tag
 * is plaintext because it is part of the address, so anything derived from the
 * recipient or from what is being shared would put that back in the clear in
 * the one field that cannot be hidden.
 *
 * ONE SHARE EVENT PER RECIPIENT PER THING, and the random name is what allows
 * that: a name derived from the recipient would collide the moment the same
 * person is sent a second collection, and the relay would replace the first
 * silently, because these are addressable events.
 */
function shareIdentifier(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Build the unsigned share event.
 *
 * Exported so the payload can be inspected in a test without a relay, and so
 * the assertion that no tag names the shared thing can be made on the event
 * rather than on a mock.
 */
export async function buildShareEvent(
  address: string,
  key: ContentKey,
  recipientPubkey: string,
  signer: SignerInterface,
  createdAt?: number
): Promise<UnsignedEvent> {
  const pubkey = await signer.getPublicKey();
  const identifier = shareIdentifier();

  // Called through the signer rather than detached into a local, because a
  // detached method loses its `this` and a lint rule rightly objects. The
  // existence check stays: NIP-44 is optional on this interface.
  if (!signer.nip44Encrypt) {
    // Same refusal as the envelope primitive, and for the same reason: the
    // fallback is NIP-04, which has no integrity check, so a tampered payload
    // would decrypt to something rather than being refused.
    throw new Error(
      'This signer cannot encrypt with NIP-44, which sharing a key requires. NIP-04 is not an acceptable substitute.'
    );
  }

  const payload: SharePayload = {
    a: address,
    e: await wrapKeyForRecipient(key, address, recipientPubkey, signer),
  };

  return {
    kind: SHARE_KIND,
    pubkey,
    created_at: createdAt ?? nextCreatedAt(`${SHARE_KIND}:${pubkey}:${identifier}`),
    // The recipient and the share's own name. Nothing about what is inside.
    tags: [
      ['d', identifier],
      ['p', recipientPubkey],
    ],
    content: await signer.nip44Encrypt(recipientPubkey, JSON.stringify(payload)),
  };
}

/** Send a key to one person. */
export async function shareWith(
  address: string,
  key: ContentKey,
  recipientPubkey: string,
  signer: SignerInterface,
  relays: readonly string[]
): Promise<SharePublishOutcome> {
  let unsigned: UnsignedEvent;
  let signed: Event;
  try {
    unsigned = await buildShareEvent(address, key, recipientPubkey, signer);
    signed = await signer.signEvent(unsigned);
  } catch (e) {
    return { status: 'signer-failed', message: message(e) };
  }

  const pubkey = unsigned.pubkey;
  const { accepted, refused } = await publishEvent([...relays], signed, (t: EventTemplate) =>
    signer.signEvent({ ...t, pubkey })
  );

  if (accepted.length === 0) {
    const reasons: Record<string, string> = {};
    for (const r of refused) reasons[r.relay] = r.reason;
    return { status: 'rejected', reasons };
  }
  return { status: 'shared', recipient: recipientPubkey, acceptedBy: accepted };
}

/**
 * Everything that has been shared with this reader.
 *
 * THE QUERY IS THE DISCOVERY MECHANISM, and this is the one place our design
 * deliberately diverges from the file service's. Stash publishes its share
 * record to the relay and then asks its own API what it holds. We ask the
 * relay, because portability is a claim about the READ path: a recipient with
 * their key and a relay address must be able to find what they were given
 * without our servers being involved at all.
 *
 * A SHARE THAT WILL NOT OPEN IS SKIPPED, NOT THROWN. The `#p` filter returns
 * everything anyone has ever addressed to this reader, including shares from
 * apps that are not this one and payloads in shapes we do not know. One
 * unreadable record is not a reason to deny the reader the others, and treating
 * it as an error would let any stranger break somebody's share list by
 * publishing a malformed event addressed to them.
 */
export async function sharesFor(
  signer: SignerInterface,
  relays: readonly string[]
): Promise<{ shares: Share[]; unreadable: number }> {
  const pubkey = await signer.getPublicKey();
  if (!signer.nip44Decrypt) {
    throw new Error('This signer cannot decrypt with NIP-44, which reading a share requires.');
  }

  const events = await fetchAll([...relays], { kinds: [SHARE_KIND], '#p': [pubkey] });

  const shares: Share[] = [];
  let unreadable = 0;

  for (const event of events) {
    const share = await openShareEvent(event, signer);
    if (share) shares.push(share);
    else unreadable++;
  }

  return { shares, unreadable };
}

/**
 * Open one share event addressed to this reader, or null if it will not open.
 *
 * Null rather than a throw for the reason `sharesFor` gives: the `#p` filter
 * returns whatever anyone addressed to this reader, in any shape, and one bad
 * record must not cost them the rest.
 */
export async function openShareEvent(event: Event, signer: SignerInterface): Promise<Share | null> {
  if (event.kind !== SHARE_KIND || !signer.nip44Decrypt) return null;
  try {
    const raw: unknown = JSON.parse(await signer.nip44Decrypt(event.pubkey, event.content));
    if (typeof raw !== 'object' || raw === null) return null;
    const { a, e } = raw as Partial<SharePayload>;
    if (typeof a !== 'string' || typeof e !== 'string') return null;

    // The envelope names what it opens, so this refuses a share whose stated
    // address disagrees with the address sealed beside the key. Following one
    // would point the recipient at one collection holding the key to another,
    // and the symptom would be "this will not open" with nothing to explain it.
    const key = await unwrapKeyFromSender(e, a, event.pubkey, signer);
    return { address: a, key, from: event.pubkey };
  } catch {
    return null;
  }
}

/**
 * Who this author has already given a key to, for one thing they own.
 *
 * WITHOUT THIS A SHARE SCREEN IS A ONE-WAY DOOR. An author can let people in and
 * has no way to find out who is already inside, which is the quiet failure this
 * repository keeps refusing elsewhere: nothing on any screen is wrong, and the
 * author's picture of who can read their private page drifts from the truth with
 * every share they forget.
 *
 * IT READS THE AUTHOR'S OWN SENT SHARES, which is possible because the NIP-44
 * conversation key is symmetric: the same two keys produce it from either end,
 * so a sender can open what they sealed to somebody else. That is a property of
 * the primitive rather than a backdoor, and it is the only reason this list can
 * exist without us keeping a copy of anything.
 *
 * IT IS A LIST OF WHO WAS SENT A KEY, NOT A LIST OF WHO CAN READ. Those differ
 * the moment a key is rotated: a recipient issued the old key is still on the
 * relay and can no longer open the current version, and a recipient who was
 * never sent anything but was told the key by other means is not here at all.
 * The caller must say it in those terms rather than presenting it as an access
 * list, because an access list we cannot actually enforce is the theatre this
 * design refuses everywhere else.
 *
 * A SHARE THAT WILL NOT OPEN IS SKIPPED, not thrown, exactly as on the reading
 * side: this author may have sent shares from another app, in a shape we do not
 * know.
 */
export async function sharesIssued(
  address: string,
  signer: SignerInterface,
  relays: readonly string[]
): Promise<string[]> {
  const recipients: string[] = [];
  for (const event of await shareEventsIssued(address, signer, relays)) {
    const to = event.tags.find((t) => t[0] === 'p')?.[1];
    if (to && !recipients.includes(to)) recipients.push(to);
  }
  return recipients;
}

/**
 * The share events this author sent for one address, so removing the thing can
 * remove them too. Without that, each one stays on the relay as a dated record
 * of who the author shared it with.
 */
export async function shareEventsIssued(
  address: string,
  signer: SignerInterface,
  relays: readonly string[]
): Promise<Event[]> {
  const pubkey = await signer.getPublicKey();
  if (!signer.nip44Decrypt) return [];
  const decrypt = signer.nip44Decrypt.bind(signer);

  const events = await fetchAll([...relays], { kinds: [SHARE_KIND], authors: [pubkey] });

  const matching: Event[] = [];
  for (const event of events) {
    if (event.pubkey !== pubkey || event.kind !== SHARE_KIND) continue;
    const to = event.tags.find((t) => t[0] === 'p')?.[1];
    if (!to || matching.some((m) => m.id === event.id)) continue;
    try {
      // Decrypted against the RECIPIENT's key, which is the other end of the
      // same exchange. Reading the `a` tag would be cheaper and there is no `a`
      // tag, deliberately: the relay is told who, never what.
      const raw: unknown = JSON.parse(await decrypt(to, event.content));
      if (typeof raw !== 'object' || raw === null) continue;
      if ((raw as Partial<SharePayload>).a === address) matching.push(event);
    } catch {
      /* not ours, or not this shape */
    }
  }
  return matching;
}

/** The key for one address, if it was shared with this reader. */
export async function sharedKeyFor(
  address: string,
  signer: SignerInterface,
  relays: readonly string[]
): Promise<ContentKey | null> {
  const { shares } = await sharesFor(signer, relays);
  // Newest wins if the same address was shared twice, which happens when a key
  // is rotated and re-issued.
  const match = shares.filter((s) => s.address === address).pop();
  return match ? match.key : null;
}

/** Only used in tests and diagnostics; never written to an event. */
export function shareKeyHex(share: Share): string {
  return contentKeyToHex(share.key);
}

function message(e: unknown): string {
  if (e instanceof Error && e.message) return e.message;
  return 'The signer refused without saying why.';
}
